import React, { useState, useEffect, useRef } from "react";
import { useHashParam } from "../utils/hashState";
import { UserPlus, CheckCircle, Clock, Award, Plus, Eye, X, Save, Upload, FileText, Download, Trash2, AlertTriangle, Check, UserCheck, CalendarClock, Camera, Search, TrendingUp } from "lucide-react";
import api from "../api";
import { uploadUrl } from "../uploadUrl";
import { K } from "../theme";
import { Cd, Av, Bg, Bt, Pr, Stat, Inp } from "../components";
import { FormModal } from "../components/FormModal";
import { useFormCatalogue } from "../formCatalog";
import { notify, confirmDialog } from "../notify";
import { CredentialsModal, credsFrom } from "../components/CredentialsModal";
import { validateFaceDetectable } from "../utils/faceValidate";
import { fmtDMY, fmtDDMMYY } from "../dateFormat";

const dn = (s) => s.is_done || s.d || false;
const sn = (s) => s.step_name || s.t || "";

// ─── Inline data-capture panels for the real onboarding steps ────────────
// These render directly under their step row so HR can do the actual work
// (upload photo, set probation period) without leaving the drawer.

const PhotoCapturePanel = ({ data, obId, onUploaded }) => {
  const [open, setOpen] = useState(false);

  const doUpload = async (blob) => {
    const fd = new FormData();
    fd.append("file", blob, "onb-profile.jpg");
    await api.upload(`/api/onboarding/${obId}/photo`, fd);
    notify("Photo uploaded", { title: "Saved", variant: "success" });
    onUploaded?.();
    setOpen(false);
  };

  return (
    <div className="mt-3 pt-3 border-t border-b0 flex items-center gap-3 flex-wrap">
      {data.photo_path
        ? <img src={uploadUrl(data.photo_path)} alt="Profile" className="w-[60px] h-[60px] rounded-full object-cover border-2" style={{ borderColor: K.g }} />
        : <div className="w-[60px] h-[60px] rounded-full bg-bg1 border border-dashed border-b1 flex items-center justify-center text-[14px] text-t3">No photo</div>
      }
      <div className="flex-1 min-w-0">
        <div className="text-[14px] text-t1">
          {data.photo_path ? "Photo captured — will appear on the employee profile after conversion." : "Capture and crop the new hire's photo. Becomes their profile photo on conversion."}
        </div>
      </div>
      <button onClick={() => setOpen(true)} className="cursor-pointer flex items-center gap-1 px-3 py-1 rounded text-[14px] font-semibold border-0" style={{ background: K.p + "14", color: K.p, border: `1px solid ${K.p}33` }}>
        <Upload size={12} />{data.photo_path ? "Replace" : "Upload Photo"}
      </button>
      {open && <OnboardingPhotoCropper obId={obId} onSaved={doUpload} onClose={() => setOpen(false)} />}
    </div>
  );
};

// Re-export of the crop modal scoped to onboarding — passes the upload
// callback so the modal stays generic (mirror of the Employees profile
// crop modal, just routing the blob to the onboarding photo endpoint).
const OnboardingPhotoCropper = ({ obId, onSaved, onClose }) => {
  const cropSize = 220;
  const [crop, setCrop] = useState({ src: null, zoom: 1, offsetX: 0, offsetY: 0, dragging: false, file: null });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const cropperRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [camOpen, setCamOpen] = useState(false);
  const [camError, setCamError] = useState("");

  useEffect(() => () => { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); }, []);

  // Clamp the crop offset so image edges always cover the circle —
  // otherwise dragging can push the image off and expose white space.
  const clampCrop = (state) => {
    const bs = state.baseScale || 1;
    const z = state.zoom || 1;
    const w = (state.imgW || cropSize) * bs * z;
    const h = (state.imgH || cropSize) * bs * z;
    const maxX = Math.max(0, (w - cropSize) / 2);
    const maxY = Math.max(0, (h - cropSize) / 2);
    return {
      ...state,
      offsetX: Math.max(-maxX, Math.min(maxX, state.offsetX || 0)),
      offsetY: Math.max(-maxY, Math.min(maxY, state.offsetY || 0)),
    };
  };

  // React's onWheel handler is registered as passive, so e.preventDefault()
  // inside it is a no-op — the modal scrolls instead of the image zooming.
  // Attach a non-passive wheel listener directly so zoom-by-scroll works.
  useEffect(() => {
    const el = cropperRef.current;
    if (!el || !crop.src) return;
    const onWheel = (e) => {
      e.preventDefault();
      setCrop(p => clampCrop({ ...p, zoom: Math.max(1, Math.min(4, p.zoom + (e.deltaY > 0 ? -0.1 : 0.1))) }));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [crop.src]);

  const onFileSelect = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.src = ev.target.result;
      img.onload = () => {
        const baseScale = cropSize / Math.min(img.width, img.height);
        setCrop(p => ({ ...p, src: ev.target.result, file, zoom: 1, offsetX: 0, offsetY: 0, imgW: img.width, imgH: img.height, baseScale }));
      };
    };
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    setCamError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 480 }, height: { ideal: 480 } } });
      streamRef.current = stream;
      setCamOpen(true);
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); } }, 0);
    } catch (e) { setCamError(e.name === "NotAllowedError" ? "Camera access denied. Allow camera access and try again." : "Could not access camera: " + e.message); }
  };
  const stopCamera = () => { if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; } setCamOpen(false); };
  const capturePhoto = () => {
    const v = videoRef.current; if (!v) return;
    const c = document.createElement("canvas"); c.width = v.videoWidth || 480; c.height = v.videoHeight || 480;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((blob) => { if (blob) onFileSelect(new File([blob], "camera-photo.jpg", { type: "image/jpeg" })); stopCamera(); }, "image/jpeg", 0.9);
  };

  const doSave = async () => {
    if (!crop.src) return;
    setBusy(true); setErr(null);
    try {
      const c = document.createElement("canvas"); c.width = 300; c.height = 300;
      const ctx = c.getContext("2d");
      const img = new Image(); img.src = crop.src;
      await new Promise(r => { img.onload = r; });
      const bs = crop.baseScale || 1; const z = crop.zoom; const totalScale = bs * z;
      const drawW = img.width * totalScale; const drawH = img.height * totalScale;
      const outScale = 300 / cropSize;
      const dx = (cropSize / 2 - drawW / 2 + crop.offsetX) * outScale;
      const dy = (cropSize / 2 - drawH / 2 + crop.offsetY) * outScale;
      ctx.beginPath(); ctx.arc(150, 150, 150, 0, Math.PI * 2); ctx.clip();
      ctx.drawImage(img, dx, dy, drawW * outScale, drawH * outScale);
      const faceCheck = await validateFaceDetectable(c);
      if (!faceCheck.ok) { setErr(faceCheck.message); setBusy(false); return; }
      const blob = await new Promise(r => c.toBlob(r, "image/jpeg", 0.9));
      await onSaved(blob);
    } catch (e) {
      setErr(e.message || "Upload failed");
      notify(e.message || "Upload failed", { title: "Error", variant: "error" });
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/60 z-[300] flex items-center justify-center p-3" onMouseDown={() => { stopCamera(); onClose(); }}>
      <div className="animate-modalIn bg-bg1 rounded-[20px] p-5 sm:p-7 w-full max-w-[600px] max-h-[90vh] overflow-auto shadow-[0_24px_80px_rgba(0,0,0,0.25)]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-5">
          <div>
            <div className="text-base font-extrabold text-t0">Upload Photo</div>
            <div className="text-[14px] text-t3">{camOpen ? "Center the face and capture" : "Drag to reposition · Scroll to zoom"}</div>
          </div>
          <button onClick={() => { stopCamera(); onClose(); }} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
        </div>
        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        {!crop.src && !camOpen ? (
          <div>
            <div
              onDragOver={e => { e.preventDefault(); }}
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) onFileSelect(f); }}
              className="border-2 border-dashed border-b1 rounded-2xl p-10 text-center cursor-pointer bg-bg2 transition-colors"
              onClick={() => { const inp = document.createElement("input"); inp.type = "file"; inp.accept = ".jpg,.jpeg,.png,.webp"; inp.onchange = e => { const f = e.target.files[0]; if (f) onFileSelect(f); }; inp.click(); }}
            >
              <Upload size={32} color={K.p} className="mx-auto mb-3" />
              <div className="text-[15px] font-bold text-t0 mb-1">Drop your photo here</div>
              <div className="text-[14px] text-t3">or click to browse · JPG, PNG, WebP</div>
              <div className="text-[12px] text-t3 mt-2">Recommended: square image, at least 300×300px</div>
            </div>
            <div className="flex items-center gap-2 my-3"><div className="flex-1 h-px bg-b1" /><span className="text-[12px] text-t3">OR</span><div className="flex-1 h-px bg-b1" /></div>
            <Bt v="secondary" full icon={Camera} onClick={startCamera}>Take Photo</Bt>
            {camError && <div className="text-[14px] mt-2 text-center" style={{ color: K.r }}>{camError}</div>}
          </div>
        ) : camOpen ? (
          <div className="flex flex-col items-center">
            <div className="rounded-full overflow-hidden border-[3px] border-p bg-bg2" style={{ width: cropSize, height: cropSize }}>
              <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }} />
            </div>
            <div className="flex gap-2 mt-4 w-full">
              <Bt v="secondary" full onClick={stopCamera}>Cancel</Bt>
              <Bt full icon={Camera} onClick={capturePhoto}>Capture</Bt>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex flex-col items-center mb-4">
              <div
                ref={cropperRef}
                className="rounded-full overflow-hidden relative cursor-grab select-none border-[3px] border-p"
                style={{ width: cropSize, height: cropSize, boxShadow: `0 0 0 4px ${K.p}20` }}
                onMouseDown={e => { e.preventDefault(); setCrop(p => ({ ...p, dragging: true, _startX: e.clientX - p.offsetX, _startY: e.clientY - p.offsetY })); }}
                onMouseMove={e => { if (!crop.dragging) return; setCrop(p => clampCrop({ ...p, offsetX: e.clientX - p._startX, offsetY: e.clientY - p._startY })); }}
                onMouseUp={() => setCrop(p => ({ ...p, dragging: false }))}
                onMouseLeave={() => setCrop(p => ({ ...p, dragging: false }))}
              >
                {(() => {
                  const bs = crop.baseScale || 1; const z = crop.zoom;
                  const w = (crop.imgW || cropSize) * bs * z;
                  const h = (crop.imgH || cropSize) * bs * z;
                  return <img src={crop.src} alt="crop" draggable={false} className="absolute pointer-events-none" style={{ left: cropSize / 2 - w / 2 + crop.offsetX, top: cropSize / 2 - h / 2 + crop.offsetY, width: w, height: h, maxWidth: 'none', maxHeight: 'none' }} />;
                })()}
              </div>
            </div>
            <div className="flex items-center gap-[10px] mb-4 px-5">
              <span className="text-[14px] text-t3">−</span>
              <input type="range" min="1" max="4" step="0.05" value={crop.zoom} onChange={e => setCrop(p => clampCrop({ ...p, zoom: parseFloat(e.target.value) }))} className="flex-1" style={{ accentColor: K.p }} />
              <span className="text-[14px] text-t3">+</span>
            </div>
            <div className="flex gap-2">
              <Bt v="secondary" full onClick={() => setCrop(p => ({ ...p, src: null, file: null, zoom: 1, offsetX: 0, offsetY: 0 }))} disabled={busy}>Change Photo</Bt>
              <Bt full icon={CheckCircle} onClick={doSave} disabled={busy}>{busy ? "Saving…" : "Save Photo"}</Bt>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ProbationSetupPanel = ({ data, obId, onSaved }) => {
  const [period, setPeriod] = useState(data.probation_period || "");
  const [endDate, setEndDate] = useState(data.probation_end_date ? data.probation_end_date.toString().slice(0, 10) : "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Auto-compute end date from period + onboarding's join_date when period
  // changes and end date is empty / matches the previous auto-derived value.
  useEffect(() => {
    if (!period || !data.join_date) return;
    const monthMap = { "1 month": 1, "3 months": 3, "6 months": 6, "12 months": 12 };
    const months = monthMap[period];
    if (!months) return;
    const join = new Date(data.join_date);
    const end = new Date(join);
    end.setMonth(end.getMonth() + months);
    setEndDate(end.toISOString().slice(0, 10));
  }, [period, data.join_date]);

  const save = async () => {
    if (!period) return setErr("Pick a probation period");
    if (!endDate) return setErr("Set probation end date");
    setBusy(true); setErr(null);
    try {
      await api.put(`/api/onboarding/${obId}/probation`, { probation_period: period, probation_end_date: endDate });
      notify("Probation details saved", { title: "Saved", variant: "success" });
      onSaved?.();
    } catch (e) {
      setErr(e.message || "Save failed");
      notify(e.message || "Save failed", { title: "Error", variant: "error" });
    } finally { setBusy(false); }
  };

  const dirty = period !== (data.probation_period || "") || endDate !== (data.probation_end_date ? data.probation_end_date.toString().slice(0, 10) : "");

  return (
    <div className="mt-3 pt-3 border-t border-b0">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[14px] text-t3 mb-1">Probation Period</div>
          <select value={period} onChange={e => setPeriod(e.target.value)} className="w-full px-2 py-1 bg-bg1 border border-b0 rounded text-[14px] outline-none">
            <option value="">Select…</option>
            <option value="1 month">1 month</option>
            <option value="3 months">3 months</option>
            <option value="6 months">6 months</option>
            <option value="12 months">12 months</option>
            <option value="Confirmed">Confirmed (no probation)</option>
          </select>
        </div>
        <div>
          <div className="text-[14px] text-t3 mb-1">Probation End Date</div>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full px-2 py-1 bg-bg1 border border-b0 rounded text-[14px] outline-none" />
        </div>
      </div>
      <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
        <div className="text-[14px] text-t3">Will copy to the employee profile on conversion — no need to re-enter.</div>
        <div className="flex items-center gap-2">
          {err && <div className="text-[14px] text-r">{err}</div>}
          <Bt v="success" s="xs" icon={Save} onClick={save} disabled={busy || !dirty}>{busy ? "Saving…" : "Save"}</Bt>
        </div>
      </div>
    </div>
  );
};


// ─── PF declarations the new joiner submitted on their joining link ───────
// Labels come from the form catalogue, so a renamed field reads right here.
const PfDeclarationsPanel = ({ data }) => {
  const { forms } = useFormCatalogue();
  const answers = data.pf_forms || null;
  if (!answers) {
    return (
      <div className="mb-4">
        <div className="text-[14px] text-t3 uppercase mb-2">PF Declarations</div>
        <div className="p-3 rounded-lg bg-bg2 border border-b0 text-[14px] text-t3">
          Not submitted yet — they're on the joining link sent with the welcome email.
        </div>
      </div>
    );
  }
  const submittedOn = data.pf_submitted_at ? fmtDMY(data.pf_submitted_at) : null;

  return (
    <div className="mb-4">
      <div className="text-[14px] text-t3 uppercase mb-2 flex items-center justify-between">
        <span>PF Declarations</span>
        {submittedOn && <span className="text-[12px] text-g normal-case font-semibold">Submitted {submittedOn}</span>}
      </div>
      {Object.entries(answers).map(([formKey, values]) => {
        const def = forms.find((f) => f.key === formKey);
        const label = (id) => {
          for (const sec of def?.sections || []) {
            const f = sec.fields.find((x) => x.id === id);
            if (f) return f.label;
          }
          return id;
        };
        return (
          <div key={formKey} className="mb-2 p-3 rounded-lg bg-bg2 border border-b0">
            <div className="text-[14px] font-bold text-t0 mb-2">{def?.name || formKey}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-[6px]">
              {Object.entries(values).map(([id, v]) => (
                <div key={id} className="min-w-0">
                  <div className="text-[12px] text-t3">{label(id)}</div>
                  <div className="text-[14px] text-t1 break-words">{v === true ? "Accepted" : v === false ? "—" : String(v)}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <div className="text-[12px] text-t3">The employer section of Form 11 (PF number, UAN, KYC status) is completed by HR outside this form.</div>
    </div>
  );
};

// Inline reject panel — open/close drawer + reason capture. Backend
// requires a non-empty reason; on success the page reloads and closes
// the detail (the row moves to the Rejected tab).
// Reschedule Joining — admin/HR moves the joining date when the candidate
// can't join on the originally-planned day (personal issues, notice-period
// extension, visa delay, etc.). Reason is mandatory and every change is
// captured in onboarding_join_reschedules so HR/CH can spot patterns.
const ReschedulePanel = ({ obId, currentJoinDate, onDone }) => {
  const [open, setOpen] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHist, setShowHist] = useState(false);

  const loadHistory = async () => {
    try {
      const rows = await api.get(`/api/onboarding/${obId}/reschedule-history`);
      setHistory(Array.isArray(rows) ? rows : []);
    } catch { /* silent — history is nice-to-have */ }
  };

  useEffect(() => { loadHistory(); }, [obId]);

  const submit = async () => {
    if (!newDate) return setErr("Pick a new joining date");
    if (reason.trim().length < 5) return setErr("Reason must be at least 5 characters");
    if (String(currentJoinDate || "").slice(0, 10) === newDate) return setErr("New date is the same as the current one");
    setBusy(true); setErr(null);
    try {
      await api.put(`/api/onboarding/${obId}/reschedule-joining`, { new_join_date: newDate, reason: reason.trim() });
      notify("Joining date rescheduled", { title: "Saved", variant: "success" });
      setOpen(false); setNewDate(""); setReason("");
      await loadHistory();
      onDone?.();
    } catch (e) {
      setErr(e.message || "Reschedule failed");
      notify(e.message || "Reschedule failed", { title: "Error", variant: "error" });
    }
    finally { setBusy(false); }
  };

  const cnt = history.length;

  if (!open) {
    return (
      <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Bt v="secondary" s="xs" icon={CalendarClock} onClick={() => setOpen(true)}>Reschedule Joining</Bt>
          {cnt > 0 && (
            <button onClick={() => setShowHist(v => !v)} className="text-[14px] text-p font-semibold bg-transparent border-0 cursor-pointer p-0 underline">
              {cnt} prior reschedule{cnt > 1 ? "s" : ""} — {showHist ? "hide" : "view"}
            </button>
          )}
        </div>
        {showHist && cnt > 0 && (
          <div className="w-full mt-1 p-2 rounded-[10px] bg-bg2 border border-b0">
            <div className="text-[14px] text-t3 uppercase mb-1">Reschedule history</div>
            <div className="flex flex-col gap-1">
              {history.map(h => (
                <div key={h.id} className="text-[14px] text-t1 border-b border-b0 last:border-0 pb-1 last:pb-0">
                  <div className="font-semibold">
                    {fmtDMY(h.old_join_date) || "—"} → {fmtDMY(h.new_join_date)}
                  </div>
                  <div className="text-t2 text-[14px]">
                    by {h.changed_by_name || h.changed_by || "system"} · {fmtDMY(h.changed_at)}, {new Date(h.changed_at).toLocaleTimeString('en-IN')}
                  </div>
                  <div className="text-t2 italic text-[14px] mt-[2px]">"{h.reason}"</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-2 p-3 rounded-[10px] bg-bg2 border border-b0">
      <div className="text-[14px] font-semibold text-t0 mb-2 flex items-center gap-1">
        <CalendarClock size={13} color={K.p} />Reschedule Joining Date
      </div>
      <div className="text-[14px] text-t3 mb-2">
        Current joining date: <strong className="text-t1">{fmtDDMMYY((currentJoinDate || "").toString().slice(0, 10)) || "not set"}</strong>.
        Reason is mandatory — the change is recorded in the audit trail.
      </div>
      <div className="grid grid-cols-1 gap-2">
        <div>
          <div className="text-[12px] text-t3 mb-1">New joining date</div>
          <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
                 className="w-full px-3 py-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] outline-none" />
        </div>
        <div>
          <div className="text-[12px] text-t3 mb-1">Reason (visible in audit trail)</div>
          <textarea value={reason} onChange={e => setReason(e.target.value)}
                    placeholder="Candidate requested extra 2 weeks — notice period extended at previous employer"
                    className="w-full min-h-[60px] p-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] outline-none" />
        </div>
      </div>
      {err && <div className="text-[14px] text-r mt-1">{err}</div>}
      <div className="flex gap-2 mt-2 justify-end">
        <Bt v="secondary" s="xs" onClick={() => { setOpen(false); setNewDate(""); setReason(""); setErr(null); }} disabled={busy}>Cancel</Bt>
        <Bt v="primary" s="xs" icon={Save} onClick={submit} disabled={busy}>{busy ? "Saving…" : "Confirm Reschedule"}</Bt>
      </div>
    </div>
  );
};

const RejectOnboardingPanel = ({ obId, onDone }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if (!reason.trim()) return setErr("Reason is required");
    if (!(await confirmDialog("Reject this onboarding? This is terminal — the record cannot be re-opened."))) return;
    setBusy(true); setErr(null);
    try {
      await api.post(`/api/onboarding/${obId}/reject`, { reason: reason.trim() });
      notify("Onboarding rejected", { title: "Saved", variant: "success" });
      onDone?.();
    } catch (e) {
      setErr(e.message || "Reject failed");
      notify(e.message || "Reject failed", { title: "Error", variant: "error" });
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="mt-3 flex justify-end">
        <Bt v="danger" s="xs" icon={AlertTriangle} onClick={() => setOpen(true)}>Reject Onboarding</Bt>
      </div>
    );
  }
  return (
    <div className="mt-3 p-3 rounded-[10px] bg-rb border border-rd">
      <div className="text-[14px] font-semibold text-r mb-2 flex items-center gap-1"><AlertTriangle size={13} />Reject Onboarding</div>
      <div className="text-[14px] text-t3 mb-2">Reason will be recorded in the audit trail. Use this when the candidate withdraws, the offer is rescinded, or there is a no-show on the join date.</div>
      <textarea
        value={reason}
        onChange={e => setReason(e.target.value)}
        placeholder="Withdrew offer / no-show on join date / company decision…"
        className="w-full min-h-[60px] p-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] outline-none"
      />
      {err && <div className="text-[14px] text-r mt-1">{err}</div>}
      <div className="flex gap-2 mt-2 justify-end">
        <Bt v="secondary" s="xs" onClick={() => { setOpen(false); setReason(""); setErr(null); }} disabled={busy}>Cancel</Bt>
        <Bt v="danger" s="xs" icon={X} onClick={submit} disabled={busy || !reason.trim()}>{busy ? "Rejecting…" : "Confirm Reject"}</Bt>
      </div>
    </div>
  );
};

const OnboardingDetail = ({ obId, onClose, onRefresh, roles = [], offices = [], companies = [], departments = [], canEdit, canDeleteNotes, canConvert, canHardDelete, canReject = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [msg, setMsg] = useState(null);
  const [docType, setDocType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [savingStep, setSavingStep] = useState(null);
  const [showConvert, setShowConvert] = useState(false);
  const [newCreds, setNewCreds] = useState(null);
  const [deletingRecord, setDeletingRecord] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  const [deletingDocId, setDeletingDocId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get(`/api/onboarding/${obId}`);
      setData(d);
    } catch (e) { setMsg({ text: e.message || "Load failed", kind: "err" }); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [obId]);

  const flash = (text, kind = "ok") => { setMsg({ text, kind }); setTimeout(() => setMsg(null), 2500); };

  const toggleStep = async (step_order, is_done) => {
    setSavingStep(step_order);
    try {
      await api.put(`/api/onboarding/${obId}/step`, { step_order, is_done: !is_done });
      await load();
      onRefresh?.();
      const doneMsg = !is_done ? "Step marked complete" : "Step reopened";
      flash(doneMsg);
      notify(doneMsg, { title: "Saved", variant: "success" });
    } catch (e) {
      flash(e.message || "Failed", "err");
      notify(e.message || "Failed to update step", { title: "Error", variant: "error" });
    } finally { setSavingStep(null); }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setSavingNotes(true);
    try {
      await api.post(`/api/onboarding/${obId}/notes`, { note: newNote.trim() });
      setNewNote("");
      flash("Remark added");
      notify("Remark added", { title: "Saved", variant: "success" });
      await load();
      onRefresh?.();
    } catch (e) {
      flash(e.message || "Save failed", "err");
      notify(e.message || "Failed to save remark", { title: "Error", variant: "error" });
    } finally { setSavingNotes(false); }
  };

  const hardDeleteOnboarding = async () => {
    const label = `${data.name} (${data.id})`;
    const msg = data.employee_id
      ? `Permanently delete onboarding record for ${label}?\n\nThis candidate has already been converted to employee ${data.employee_id}. Deleting only removes the onboarding history (steps, documents, remarks) — the employee account itself stays.\n\nThis cannot be undone.`
      : `Permanently delete onboarding record for ${label}?\n\nThis removes the record plus all steps, documents, and remarks. Cannot be undone.`;
    if (!(await confirmDialog(msg))) return;
    setDeletingRecord(true);
    try {
      await api.del(`/api/onboarding/${obId}`);
      notify("Onboarding record deleted", { title: "Saved", variant: "success" });
      onRefresh?.();
      onClose();
    } catch (e) {
      flash(e.message || "Delete failed", "err");
      notify(e.message || "Delete failed", { title: "Error", variant: "error" });
      setDeletingRecord(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!(await confirmDialog("Delete this remark?"))) return;
    setDeletingNoteId(noteId);
    try {
      await api.del(`/api/onboarding/${obId}/notes/${noteId}`);
      flash("Remark removed");
      notify("Remark removed", { title: "Saved", variant: "success" });
      await load();
    } catch (e) {
      flash(e.message || "Delete failed", "err");
      notify(e.message || "Failed to delete remark", { title: "Error", variant: "error" });
    } finally { setDeletingNoteId(null); }
  };

  const handleUpload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { flash(`File too large: ${(f.size / 1024 / 1024).toFixed(2)} MB. Max 10 MB.`, "err"); e.target.value = ""; return; }
    if (!/\.(pdf|doc|docx|png|jpe?g)$/i.test(f.name)) { flash("Unsupported file type. Use PDF, JPG, PNG, DOC.", "err"); e.target.value = ""; return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (docType) fd.append("doc_type", docType);
      await api.upload(`/api/onboarding/${obId}/documents`, fd);
      flash("Document uploaded");
      notify("Document uploaded", { title: "Saved", variant: "success" });
      setDocType("");
      e.target.value = "";
      await load();
      onRefresh?.();
    } catch (err) {
      flash(err.message || "Upload failed", "err");
      notify(err.message || "Upload failed", { title: "Error", variant: "error" });
    } finally { setUploading(false); }
  };

  const deleteDoc = async (docId, name) => {
    if (!(await confirmDialog(`Delete document "${name}"?`))) return;
    setDeletingDocId(docId);
    try {
      await api.del(`/api/onboarding/${obId}/documents/${docId}`);
      flash("Document deleted");
      notify("Document deleted", { title: "Saved", variant: "success" });
      await load();
      onRefresh?.();
    } catch (e) {
      flash(e.message || "Delete failed", "err");
      notify(e.message || "Failed to delete document", { title: "Error", variant: "error" });
    } finally { setDeletingDocId(null); }
  };

  if (loading || !data) {
    return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-bg1 rounded-2xl p-6 text-t2 text-[15px]">{loading ? "Loading…" : "Record not found"}</div>
    </div>;
  }

  // Once the record has been graduated to an employee, freeze all edits —
  // no step toggles, no doc upload/remove, no new remarks. The admin should
  // manage the converted employee from the Employees module instead.
  // Both terminal stages lock the record. Converted = success, Rejected =
  // terminated. Either way: no more step toggles, photo uploads, probation
  // edits, doc actions, or notes — same UX as a converted record.
  const locked = !!data.employee_id || data.stage === 'Rejected';
  const writable = canEdit && !locked;

  const totalSteps = data.steps.length || 1;
  const doneSteps = data.steps.filter(dn).length;
  const pct = Math.round((doneSteps / totalSteps) * 100);
  const allDone = doneSteps === totalSteps;

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl w-full max-w-[1104px] max-h-[90vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-b0 sticky top-0 bg-bg1 z-10">
          <div className="flex items-center gap-3 min-w-0">
            <Av nm={data.name} sz={42} rn={allDone ? K.g : K.bl} />
            <div className="min-w-0">
              <div className="text-[17px] font-extrabold text-t0 truncate">{data.name}</div>
              <div className="text-[14px] text-t2">{data.position} · {data.office}</div>
              <div className="text-[14px] text-t3 mt-[1px]">Joining: <strong>{fmtDMY(data.join_date)}</strong></div>
              {data.candidate_id && <div className="text-[14px] text-p mt-[2px]">
                Sourced from candidate <strong>{data.candidate_id}</strong>
                {data.request_id && <> · Requisition <strong>#{data.request_id}</strong>{data.req_position ? ` (${data.req_position})` : ""}</>}
              </div>}
              {/* CV carried over from the recruitment side. Lives on the
                  candidate row (candidates.resume_path), not in
                  onboarding_documents — surfaced here through the link so
                  HR doesn't have to jump back to Recruitment to read it. */}
              {data.candidate_resume_path && (
                <a href={uploadUrl(data.candidate_resume_path)} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-[14px] text-p font-semibold no-underline hover:underline mt-[3px]">
                  <FileText size={11} /> View CV
                </a>
              )}
            </div>
          </div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={20} /></button>
        </div>

        <div className="p-5">
          {msg && <div className={`px-3 py-2 mb-3 rounded-lg text-[14px] font-semibold ${msg.kind === "err" ? "bg-rb text-r border border-rd" : "bg-gb text-g border border-gd"}`}>{msg.text}</div>}

          {/* Stage + progress */}
          <div className="flex items-center justify-between mb-2">
            <Bg c={allDone ? K.g : pct >= 50 ? K.bl : K.a}>{data.stage}</Bg>
            <div className={`text-[14px] font-bold ${allDone ? "text-g" : "text-t2"}`}>{pct}% · {doneSteps}/{totalSteps} steps</div>
          </div>
          <Pr value={pct} c={allDone ? K.g : pct >= 50 ? K.bl : K.a} h={5} />

          {/* Graduation CTA — shown when steps are done and no employee row exists yet */}
          {data.employee_id ? (
            <div className="mt-3 p-3 rounded-[10px] bg-gb border border-gd flex items-center gap-2">
              <UserCheck size={16} color={K.g} />
              <div className="text-[14px]"><span className="font-semibold text-g">Converted to employee {data.employee_id}</span><span className="text-t2"> — this record is locked.</span></div>
            </div>
          ) : data.stage === 'Rejected' ? (
            <div className="mt-3 p-3 rounded-[10px] bg-rb border border-rd">
              <div className="flex items-center gap-2"><AlertTriangle size={16} color={K.r} /><div className="text-[14px] font-semibold text-r">Onboarding rejected{data.closed_at ? ` on ${fmtDMY(data.closed_at)}` : ""}</div></div>
              {data.rejection_reason && <div className="text-[14px] text-t1 mt-2">Reason: {data.rejection_reason}</div>}
            </div>
          ) : (allDone && canConvert) ? (
            <div className="mt-3 p-3 rounded-[10px] bg-pg border border-pb flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[14px] text-t0"><span className="font-semibold">All steps complete.</span> Convert this candidate into an active employee.</div>
              <Bt v="primary" s="sm" icon={UserCheck} onClick={() => setShowConvert(true)}>Convert to Employee</Bt>
            </div>
          ) : null}

          {/* Reschedule joining — HR moves the join date with reason + history */}
          {!locked && canReject && (
            <ReschedulePanel obId={obId} currentJoinDate={data.join_date} onDone={() => { load(); onRefresh?.(); }} />
          )}

          {/* Reject onboarding (in-progress only — terminal stages can't be rejected) */}
          {!data.employee_id && data.stage !== 'Rejected' && canReject && (
            <RejectOnboardingPanel obId={obId} onDone={() => { onRefresh?.(); onClose(); }} />
          )}

          {/* Steps checklist */}
          <div className="mt-4 mb-4">
            <div className="text-[14px] text-t3 uppercase mb-2">Onboarding Steps</div>
            <div className="flex flex-col gap-2">
              {data.steps.map(s => {
                const done = dn(s);
                const stepBusy = savingStep === s.step_order;
                const name = sn(s);
                const isPhoto = name === "Photo Capture";
                const isProbation = name === "Training Setup";
                return (
                  <div
                    key={s.step_order}
                    className={`px-3 py-[10px] rounded-lg border ${done ? "bg-gb border-gd" : "bg-bg2 border-b0"}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {done ? <CheckCircle size={16} color={K.g} /> : <Clock size={16} color={K.t3} />}
                        <span className={`text-[14px] font-semibold ${done ? "text-g" : "text-t1"}`}>{name}</span>
                      </div>
                      {writable ? (
                        done ? (
                          <Bt v="ghost" s="xs" onClick={() => toggleStep(s.step_order, true)} disabled={stepBusy}>{stepBusy ? "…" : "Reopen"}</Bt>
                        ) : (
                          <Bt v="success" s="xs" icon={Check} onClick={() => toggleStep(s.step_order, false)} disabled={stepBusy}>{stepBusy ? "Saving…" : "Mark Complete"}</Bt>
                        )
                      ) : (
                        <span className={`text-[14px] font-semibold ${done ? "text-g" : "text-t3"}`}>{done ? "Done" : "Pending"}</span>
                      )}
                    </div>

                    {isPhoto && writable && <PhotoCapturePanel data={data} obId={obId} onUploaded={load} />}
                    {isProbation && writable && <ProbationSetupPanel data={data} obId={obId} onSaved={load} />}
                  </div>
                );
              })}
            </div>
          </div>

          <PfDeclarationsPanel data={data} />

          {/* Documents */}
          <div className="mb-4">
            <div className="text-[14px] text-t3 uppercase mb-2 flex items-center justify-between">
              <span>Documents ({data.documents?.length || 0})</span>
              <span className="text-[12px] text-t3 normal-case">PDF/JPG/PNG/DOC · Max 10 MB</span>
            </div>

            {writable && <div className="flex gap-2 items-end mb-2">
              <div className="flex-1">
                <div className="text-[12px] text-t3 mb-1">Document type (optional)</div>
                <select value={docType} onChange={e => setDocType(e.target.value)} className="w-full px-3 py-2 bg-bg2 border border-b0 rounded-[10px] text-[14px] outline-none">
                  <option value="">— select —</option>
                  <option value="aadhaar">Aadhaar</option>
                  <option value="pan">PAN</option>
                  <option value="offer_letter">Offer Letter</option>
                  <option value="prev_exp">Prev Experience Letter</option>
                  <option value="education">Education Certificate</option>
                  <option value="resume">Resume</option>
                  <option value="photo">Photo</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <label className="flex items-center gap-2 px-4 py-2 rounded-[10px] bg-p text-white text-[14px] font-semibold cursor-pointer whitespace-nowrap">
                <Upload size={13} />{uploading ? "Uploading…" : "Upload"}
                <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="hidden" onChange={handleUpload} disabled={uploading} />
              </label>
            </div>}

            {(!data.documents || data.documents.length === 0) ? (
              <div className="text-center py-6 text-t3 text-[14px] bg-bg2 rounded-lg border border-b0 border-dashed">No documents uploaded yet</div>
            ) : (
              <div className="flex flex-col gap-[6px]">
                {data.documents.map(d => (
                  <div key={d.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-[10px] bg-bg2 border border-b0">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText size={16} color={K.p} />
                      <div className="min-w-0">
                        <div className="text-[14px] font-semibold text-t0 truncate">{d.file_name}</div>
                        <div className="text-[12px] text-t3">{d.doc_type ? d.doc_type + " · " : ""}{d.size_bytes ? (d.size_bytes / 1024).toFixed(0) + " KB" : ""}{d.uploaded_at ? " · " + fmtDMY(d.uploaded_at) : ""}</div>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <a href={uploadUrl(d.file_path)} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-2 py-1 rounded text-[14px] font-semibold text-bl bg-transparent border border-b0 no-underline"><Download size={11} />View</a>
                      {writable && <button onClick={() => deleteDoc(d.id, d.file_name)} disabled={deletingDocId === d.id} className="flex items-center gap-1 px-2 py-1 rounded text-[14px] font-semibold text-r bg-transparent border border-rd cursor-pointer disabled:opacity-50">{deletingDocId === d.id ? <>Removing…</> : <><Trash2 size={11} />Remove</>}</button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Remarks / Notes — append-only thread so every saved remark is preserved with author + timestamp */}
          <div className="mb-2">
            <div className="text-[14px] text-t3 uppercase mb-[6px]">Remarks / Notes ({data.notes_thread?.length || 0})</div>

            {writable && <div className="mb-3">
              <textarea
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                placeholder="Add a new remark…"
                className="w-full min-h-[80px] p-3 bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none resize-y"
              />
              <div className="mt-2">
                <Bt v="success" s="sm" icon={Save} onClick={addNote} disabled={savingNotes || !newNote.trim()}>{savingNotes ? "Saving…" : "Save Remark"}</Bt>
              </div>
            </div>}

            {/* Thread — newest first */}
            {(!data.notes_thread || data.notes_thread.length === 0) ? (
              // Fall back to legacy single-field value for old records that predate the thread
              data.notes ? (
                <div className="px-3 py-2 rounded-[10px] bg-bg2 border border-b0">
                  <div className="text-[12px] text-t3 mb-1">Legacy note (pre-thread)</div>
                  <div className="text-[14px] text-t1 whitespace-pre-wrap">{data.notes}</div>
                </div>
              ) : (
                <div className="text-center py-4 text-t3 text-[14px] bg-bg2 rounded-lg border border-b0 border-dashed">No remarks yet</div>
              )
            ) : (
              <div className="flex flex-col gap-[6px]">
                {data.notes_thread.map(n => (
                  <div key={n.id} className="px-3 py-2 rounded-[10px] bg-bg2 border border-b0 border-l-2 border-l-p">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="text-[14px] text-t2 font-semibold">{n.created_by_name || n.created_by || "—"}</div>
                      <div className="flex items-center gap-2">
                        <div className="text-[12px] text-t3">{fmtDMY(n.created_at)}, {new Date(n.created_at).toLocaleTimeString('en-IN')}</div>
                        {canDeleteNotes && !locked && <button onClick={() => deleteNote(n.id)} disabled={deletingNoteId === n.id} className="flex items-center gap-1 px-[6px] py-[2px] rounded text-[12px] font-semibold text-r bg-transparent border border-rd cursor-pointer disabled:opacity-50"><Trash2 size={10} /></button>}
                      </div>
                    </div>
                    <div className="text-[14px] text-t0 whitespace-pre-wrap">{n.note}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {!canEdit && <div className="text-[14px] text-t3 text-center py-2 mt-3 border-t border-b0">View only — actions restricted to Admin / HR Head / Recruiter</div>}

          {/* Admin-only permanent delete — works even if the record is locked (already converted) */}
          {canHardDelete && <div className="border-t border-b0 pt-4 mt-4">
            <div className="text-[14px] text-t3 uppercase mb-2">Admin actions</div>
            <Bt v="danger" s="sm" icon={Trash2} onClick={hardDeleteOnboarding} disabled={deletingRecord}>{deletingRecord ? "Deleting…" : "Permanently Delete Candidate"}</Bt>
            <div className="text-[12px] text-t3 mt-1">Removes the onboarding record, its steps, documents and remarks from the database. {data.employee_id ? `The linked employee ${data.employee_id} is not affected.` : "Cannot be undone."}</div>
          </div>}
        </div>
      </div>

      {newCreds && <CredentialsModal items={newCreds} onClose={() => setNewCreds(null)} />}
      {showConvert && <ConvertModal ob={data} roles={roles} offices={offices} companies={companies} departments={departments} onClose={() => setShowConvert(false)} onDone={async (emp) => {
        flash(`Converted to ${emp.employee_id}.${emp.emailed_to ? ` Login emailed to ${emp.emailed_to}.` : ""}`);
        notify(`Converted to ${emp.employee_id}.${emp.emailed_to ? ` Login emailed to ${emp.emailed_to}.` : ""}`, { title: "Saved", variant: "success" });
        { const c = credsFrom(emp, data?.name); if (c) setNewCreds([c]); }
        setShowConvert(false);
        await load();
        onRefresh?.();
      }} />}
    </div>
  );
};

// Pre-filled employee-creation form used when graduating an onboarding record.
const ConvertModal = ({ ob, roles = [], offices = [], companies = [], departments = [], onClose, onDone }) => {
  // Seed what we already know from onboarding; admin fills the rest.
  const roleGuess = roles.find(r => (r.t || r.title || "").toLowerCase() === (ob.position || "").toLowerCase());
  const officeGuess = offices.find(o => (o.name || o.nm || "").toLowerCase() === (ob.office || "").toLowerCase());
  // Phone + personal email come from the linked candidate row via the
  // onboarding list join (candidate_phone / candidate_email). HR shouldn't
  // have to retype what recruitment already captured.
  const initial = {
    role_id: roleGuess?.id || "",
    office_id: officeGuess ? String(officeGuess.id) : "",
    join_date: (ob.join_date || "").toString().slice(0, 10),
    employee_type: "Full Time",
    phone: ob.candidate_phone || "",
    personal_email: ob.candidate_email || "",
  };

  return <FormModal
    title={`Convert "${ob.name}" → Employee`}
    initialValues={initial}
    fields={[
      { key: "email", label: "Company Email * (becomes login)", placeholder: "rajesh@company.com" },
      { key: "role_id", label: "Role *", type: "select", options: [{ v: "", l: "Select Role" }, ...roles.filter(r => r.active !== false).map(r => ({ v: r.id, l: r.t || r.title }))] },
      { key: "department_id", label: "Department *", type: "select", options: [{ v: "", l: "Select Department" }, ...departments.filter(d => d.is_active !== false).map(d => ({ v: String(d.id), l: d.name }))] },
      { key: "office_id", label: "Office *", type: "select", options: [{ v: "", l: "Select Office" }, ...offices.map(o => ({ v: String(o.id), l: (o.nm || o.name) + " — " + (o.city || "") }))] },
      { key: "company_id", label: "Company *", type: "select", options: [{ v: "", l: "Select Company" }, ...companies.map(c => ({ v: String(c.id), l: c.name }))] },
      { key: "join_date", label: "Join Date *", type: "date" },
      { key: "employee_type", label: "Employee Type *", type: "select", options: [{ v: "", l: "Select" }, { v: "Full Time", l: "Full Time" }, { v: "Part Time", l: "Part Time" }, { v: "Contract", l: "Contract" }, { v: "Intern", l: "Intern" }] },
      { key: "phone", label: "Phone", placeholder: "+91 98765 43210" },
      { key: "personal_email", label: "Personal Email", placeholder: "rajesh@gmail.com" },
      { key: "gender", label: "Gender", type: "select", options: [{ v: "", l: "Select" }, { v: "M", l: "Male" }, { v: "F", l: "Female" }, { v: "O", l: "Other" }] },
    ]}
    onSubmit={async v => {
      const missing = [["email", "Company Email"], ["role_id", "Role"], ["department_id", "Department"], ["office_id", "Office"], ["company_id", "Company"], ["join_date", "Join Date"], ["employee_type", "Employee Type"]].find(([k]) => !v[k] || String(v[k]).trim() === "");
      if (missing) throw new Error(`${missing[1]} is required`);
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim())) throw new Error("Invalid company email format");
      try {
        const res = await api.post(`/api/onboarding/${ob.id}/convert`, {
          ...v,
          email: v.email.trim(),
          office_id: parseInt(v.office_id),
          company_id: parseInt(v.company_id),
          department_id: parseInt(v.department_id),
        });
        onDone?.(res);
      } catch (e) {
        notify(e.message || "Conversion failed", { title: "Error", variant: "error" });
        throw e;
      }
    }}
    onClose={onClose}
  />;
};

export const OnboardingPage = ({ roles = [], offices = [], companies = [], departments = [], role, onRefresh, hashQuery, can = () => true }) => {
  const [showForm, setShowForm] = useState(null);
  // Deep link from a notification (e.g. "X submitted their joining details
  // form") — #onboarding?id=<onboarding row id> opens that record directly
  // instead of leaving the viewer to hunt for it in the list. Open record
  // and active tab both live in the URL hash, so a refresh keeps them and
  // Back closes the record / returns to the previous tab.
  const [openOb, setOpenOb, closeOb] = useHashParam("id");
  const [tab, setTab] = useHashParam("tab", "active");
  // Default date window for terminal buckets — last 90 days. Active ignores
  // dates by default since "active" is intrinsically a small working set.
  const ninetyDaysAgo = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(ninetyDaysAgo);
  const [to, setTo] = useState(today);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ active: 0, completed: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);

  const canEdit = can('onboarding', null, null, 'edit');
  // Centre Head can convert too, but only within their own centre — the
  // list they see (and the backend /convert check) is already scoped to
  // that, so no extra client-side filtering is needed here.
  const canConvert = can('onboarding', null, 'convert_to_employee', 'edit');

  const dc = (o) => (o.steps || []).filter(dn).length;
  const tc = (o) => (o.steps || []).length || 7;
  const pc = (o) => Math.round(dc(o) / tc(o) * 100);

  const officeOpts = (offices && offices.length
    ? offices.map(o => ({ v: o.name || o.nm, l: (o.nm || o.name) + " — " + (o.city || "") }))
    : []);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ status: tab });
    if (tab !== "active") { params.set("from", from); params.set("to", to); }
    api.get(`/api/onboarding?${params.toString()}`)
      .then(d => { setRows(d.rows || []); setCounts(d.counts || { active: 0, completed: 0, rejected: 0 }); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, from, to]);

  const [obSearch, setObSearch] = useState("");
  useEffect(() => { setObSearch(""); }, [tab]);
  const sq = obSearch.trim().toLowerCase();
  const filteredRows = sq ? rows.filter(ob => `${ob.name || ob.nm || ""} ${ob.position || ob.pos || ""} ${ob.office || ob.ofc || ""}`.toLowerCase().includes(sq)) : rows;

  const daysUntil = (d) => { if (!d) return null; const diff = Math.ceil((new Date(d) - new Date(today)) / 86400000); return diff; };
  const turnaroundDays = rows.filter(o => o.created_at && (o.join_date || o.jd)).map(o => Math.round((new Date(o.join_date || o.jd) - new Date(o.created_at)) / 86400000));
  const avgTurnaround = turnaroundDays.length ? Math.round(turnaroundDays.reduce((s, d) => s + d, 0) / turnaroundDays.length * 10) / 10 : null;

  const exportCsv = () => {
    const header = ["Name", "Position", "Office", "Join Date", "Stage", "Progress"];
    const csvRows = filteredRows.map(ob => [ob.name || ob.nm, ob.position || ob.pos, ob.office || ob.ofc, fmtDMY(ob.join_date || ob.jd), ob.stage || ob.stg, `${pc(ob)}%`]);
    const csv = [header, ...csvRows].map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `onboarding_${tab}_${today}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const tabList = [
    { k: "active", l: "Active", count: counts.active },
    { k: "completed", l: "Completed", count: counts.completed },
    { k: "rejected", l: "Rejected / Dropped", count: counts.rejected },
  ];

  return (
    <div>
      <div className="flex justify-between items-start mb-4 flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-[10px]">
            <h1 className="m-0 text-2xl font-extrabold text-t0">Onboarding Pipeline</h1>
            <Bg c={K.p} sm>{counts.active} in pipeline</Bg>
          </div>
          <p className="mt-[3px] mb-0 text-t2 text-[14px] max-w-2xl">Track candidate document collection, e-sign agreements, and provisioning ahead of Day 1.</p>
        </div>
        <div className="flex gap-[6px]">
          <Bt v="secondary" icon={Download} onClick={exportCsv}>Export CSV</Bt>
          {canEdit && <Bt icon={Plus} onClick={() => setShowForm("onboarding")}>Initiate Onboarding</Bt>}
        </div>
      </div>

      {tab === "active" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          {[
            { icon: UserPlus,    label: "Active",         value: rows.length, c: K.bl, sub: "In pipeline right now" },
            { icon: CheckCircle, label: "Avg Progress",   value: `${rows.length ? Math.round(rows.reduce((s, o) => s + pc(o), 0) / rows.length) : 0}%`, c: K.g,  sub: "Overall checklist throughput" },
            { icon: Clock,       label: "Pending Steps",  value: rows.reduce((s, o) => s + tc(o) - dc(o), 0), c: K.a,  sub: "Across all active candidates" },
            { icon: Award,       label: "Fully Complete", value: rows.filter(o => pc(o) === 100).length, c: K.tl, sub: "Ready for Day 1" },
            { icon: TrendingUp,  label: "Avg Turnaround", value: avgTurnaround != null ? `${avgTurnaround}d` : "—", c: K.pu, sub: "Initiated → joining date" },
          ].map(s => (
            <div key={s.label} className="bg-s rounded-xl p-4 border border-b0 flex items-center gap-3 shadow-[0_2px_4px_rgba(0,0,0,0.02)]" style={{ borderLeft: `4px solid ${s.c}` }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${s.c}14`, border: `1px solid ${s.c}2a`, color: s.c }}>
                <s.icon size={19} />
              </div>
              <div className="min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-extrabold text-t0 leading-none">{s.value}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: s.c }}>{s.label}</span>
                </div>
                <p className="text-[11px] text-t3 mt-1 font-medium truncate">{s.sub}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="inline-flex p-1 bg-bg2 rounded-xl gap-[2px]">
          {tabList.map(t => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-4 py-[7px] rounded-lg text-[13px] font-bold transition-colors flex items-center gap-[6px] cursor-pointer border-0 whitespace-nowrap ${tab === t.k ? "bg-s text-t0 shadow-[0_1px_3px_rgba(0,0,0,0.1)]" : "bg-transparent text-t2 hover:text-t0"}`}
            >{t.l} <span className="px-[6px] py-[1px] rounded-full text-[10px] font-extrabold bg-b1 text-t2">{t.count}</span></button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {tab !== "active" && (
            <div className="flex items-center gap-2">
              <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="px-2 py-1 bg-bg1 border border-b0 rounded text-[13px] outline-none" />
              <span className="text-[13px] text-t3">to</span>
              <input type="date" value={to} onChange={e => setTo(e.target.value)} className="px-2 py-1 bg-bg1 border border-b0 rounded text-[13px] outline-none" />
              <button onClick={() => { setFrom(ninetyDaysAgo); setTo(today); }} className="text-[12px] text-p font-semibold bg-transparent border-0 cursor-pointer whitespace-nowrap">Reset 90d</button>
            </div>
          )}
          <div className="w-[220px]"><Inp icon={Search} placeholder="Filter by candidate, role…" value={obSearch} onChange={setObSearch} /></div>
        </div>
      </div>

      {loading && <div className="text-center p-[30px] text-t3 text-[14px]">Loading…</div>}
      {!loading && filteredRows.map(ob => {
        const p = pc(ob);
        const du = tab === "active" ? daysUntil(ob.join_date || ob.jd) : null;
        return (
          <Cd key={ob.id} style={{ marginBottom: 12 }}>
            <div className="flex justify-between items-center gap-3 flex-wrap">
              <div className="flex items-center gap-[10px] flex-1 min-w-[220px]">
                <Av nm={ob.name || ob.nm} sz={40} rn={p === 100 ? K.g : K.bl} />
                <div className="min-w-0">
                  <div className="text-[15px] font-bold text-t0 truncate">{ob.name || ob.nm}</div>
                  <div className="text-[14px] text-t2 truncate">{ob.position || ob.pos} · {ob.office || ob.ofc}</div>
                  <div className="flex items-center gap-[6px] mt-[3px] flex-wrap">
                    <span className="text-[12px] text-t3">Joining: <strong className="text-t1">{fmtDMY(ob.join_date || ob.jd)}</strong></span>
                    {du != null && <Bg c={du < 0 ? K.r : du <= 7 ? K.a : K.p} sm>{du < 0 ? `${-du}d overdue` : du === 0 ? "Today" : `In ${du} days`}</Bg>}
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Bg c={ob.stage === 'Rejected' ? K.r : (p === 100 ? K.g : p >= 50 ? K.bl : K.a)}>{ob.stage || ob.stg}</Bg>
                <div className={`text-[14px] font-bold ${p === 100 ? "text-g" : "text-t2"}`}>
                  {tab === "active" ? `${p}% · ${dc(ob)}/${tc(ob)}` :
                    tab === "completed" ? `Joined ${fmtDMY(ob.closed_at || ob.created_at)}` :
                    `Rejected ${fmtDMY(ob.closed_at)}`}
                </div>
              </div>
              {tab === "active" && <div className="w-full"><Pr value={p} c={p === 100 ? K.g : p >= 50 ? K.bl : K.a} h={4} /></div>}
              {tab === "rejected" && ob.rejection_reason && <div className="w-full text-[14px] text-r bg-rb border border-rd rounded px-2 py-1">Reason: {ob.rejection_reason}</div>}
              <div className="flex gap-2 ml-auto flex-wrap">
                {ob.candidate_id && <span className="text-[14px] text-p flex items-center gap-1 px-2 py-1 bg-bg2 rounded border border-b0" title={`Sourced from candidate ${ob.candidate_id}${ob.req_id ? ` · Requisition #${ob.req_id}` : ""}`}>Sourced from {ob.candidate_id}{ob.req_id ? ` · Req #${ob.req_id}` : ""}</span>}
                {ob.employee_id && <span className="text-[14px] text-g flex items-center gap-1 px-2 py-1 bg-gb rounded border border-gd">→ Employee {ob.employee_id}</span>}
                {ob.doc_count > 0 && <span className="text-[14px] text-t2 flex items-center gap-1 px-2 py-1 bg-bg2 rounded border border-b0"><FileText size={11} />{ob.doc_count} doc{ob.doc_count !== 1 ? "s" : ""}</span>}
                <Bt v="ghost" s="sm" icon={Eye} onClick={() => setOpenOb(ob.id)}>View</Bt>
              </div>
            </div>
          </Cd>
        );
      })}

      {!loading && filteredRows.length === 0 && <div className="text-center p-[30px] text-t3 text-[14px]">
        {rows.length > 0 ? "No candidates match your search" :
         tab === "active" ? "No active onboarding records" :
         tab === "completed" ? "No completed onboardings in this period" :
         "No rejected onboardings in this period"}
      </div>}

      {openOb && <OnboardingDetail obId={openOb} onClose={closeOb} onRefresh={() => { load(); onRefresh?.(); }} roles={roles} offices={offices} companies={companies} departments={departments} canEdit={canEdit} canDeleteNotes={can('onboarding', null, 'delete_note', 'edit')} canConvert={canConvert} canHardDelete={can('onboarding', null, 'hard_delete', 'edit')} canReject={can('onboarding', null, 'reject_reschedule', 'edit')} />}

      {showForm === "onboarding" && (
        <FormModal
          title="New Onboarding"
          fields={[
            { key: "name", label: "Full Name *", placeholder: "Rajesh Kumar" },
            { key: "position", label: "Position *", type: "select", options: [{ v: "", l: "Select Position" }, ...roles.map(r => ({ v: r.t || r.title, l: r.t || r.title }))] },
            { key: "company_id", label: "Company *", type: "select", options: [{ v: "", l: "Select Company" }, ...companies.map(c => ({ v: String(c.id), l: c.name }))] },
            { key: "office_id",  label: "Office *",  type: "select", options: [{ v: "", l: "Select Office" }, ...offices.map(o => ({ v: String(o.id), l: (o.nm || o.name) + " — " + (o.city || "") }))] },
            { key: "join_date", label: "Joining Date *", type: "date" },
          ]}
          onSubmit={async v => {
            if (!v.name || !v.position || !v.company_id || !v.office_id || !v.join_date) throw new Error("All fields required");
            // Populate the free-text `office` display value from the picked office
            // so existing list rows still show a readable branch label.
            const off = offices.find(o => String(o.id) === String(v.office_id));
            const officeLabel = off ? (off.nm || off.name) : "";
            try {
              await api.post("/api/onboarding", { ...v, office: officeLabel });
              notify("Onboarding record created", { title: "Saved", variant: "success" });
              load();
              onRefresh?.();
            } catch (e) {
              notify(e.message || "Failed to create onboarding record", { title: "Error", variant: "error" });
              throw e;
            }
          }}
          onClose={() => setShowForm(null)}
        />
      )}
    </div>
  );
};
